<template>
  <RoutinePage v-show="!isRestricted" :entry="entry" />
</template>
<script>
import gql from 'graphql-tag';
import entryPage, { SeomaticFragment, EntryPageFragment } from '@/mixins/entryPage';
import { stripLocale } from '@/utils/i18n';
import { enrichBlocksWithExerciseData } from '@/utils/algolia';
import RoutinePage, { RoutinePageFragment } from '@/components/routine/RoutinePage';

export const RoutinePageQuery = gql`
  query RoutinePageQuery($site: String!, $uri: String!) {
    entry(section: "routine", site: [$site], uri: [$uri]) {
      title
      ...EntryPageFragment
      ...RoutinePageFragment
    }

    seomatic(site: $site, uri: $uri, asArray: true) {
      ...SeomaticFragment
    }
  }

  ${EntryPageFragment}
  ${RoutinePageFragment}
  ${SeomaticFragment}
`;

export default {
  name: 'RoutinePageController',
  components: {
    RoutinePage,
  },
  mixins: [entryPage],

  async asyncData({ $craftRequest, $retourRedirect, $algoliaWorkouts, app, params, error, route }) {
    const { locale } = app.i18n;
    const uri = stripLocale(route.path, locale);
    const variables = { site: locale, uri };
    const { entry, seomatic } = await $craftRequest(RoutinePageQuery, variables);

    if (entry === null) {
      return $retourRedirect();
    }

    if (entry.pimWorkout) {
      // save default block in new variable
      const additionalBlocks = [...entry.exercisesBlocks];
      // get algolia data for workout
      const workout = await $algoliaWorkouts.fetchFegWorkoutById(entry.pimWorkout);

      // after fetching workout from algolia, we need to map it to the correct format
      // for the exercises block component
      const blocks = workout.exercises.map((exercise) => {
        return {
          typeHandle: 'exercise',
          exerciseObjectId: exercise.workoutExerciseId,
          heading: exercise.blockHeading,
          description: exercise.blockDescription,
          title: exercise.titleOverwrite ?? exercise.title,
          shortDescription: exercise.shortDescriptionOverwrite ?? exercise.shortDescription,
          longDescription: exercise.descriptionOverwrite ?? exercise.longDescription,
          practiceLabelKey: exercise.practice?.length > 0 ? exercise.practice : null,
          practiceValue: exercise.repetitions
            ? `${exercise.repetitions}`
            : exercise.sets && exercise.sets && exercise.seconds
            ? `${exercise.sets} / ${exercise.seconds}`
            : exercise.seconds > 0
            ? `${exercise.seconds}`
            : null,
        };
      });

      // merge default blocks with exercise blocks from algolia
      additionalBlocks.forEach((block) => {
        if (block.position) {
          // assume that we get position counting from 1 to n
          // reduce pos by 1, so we can add it to the front of the array :)
          const position = Math.max(0, block.position - 1);
          blocks.splice(position, 0, block);
        } else {
          blocks.push(block);
        }
      });

      // assign the blocks to the entry
      entry.exercisesBlocks = blocks;
    }

    // enrich blocks with algolia exercise data
    await enrichBlocksWithExerciseData(entry.exercisesBlocks, $algoliaWorkouts);

    return { entry, seomatic };
  },
  mounted() {
    this.setBreadCrumb();
    this.gtmPushPageMeta({ content_category: 'exercises' });
  },
};
</script>
